import { useState } from 'preact/hooks';
import { cn } from '../lib/cn';
import type { PreChatFormConfig, PreChatFormField } from '../client/api-types';
import type { PreChatData } from './useChatState';
import { t } from '../lib/i18n';
import { validateFields } from '../lib/field-validation';

interface PreChatFormProps {
  config: PreChatFormConfig;
  brandingColor: string;
  brandingName?: string;
  brandingLogo?: string | null;
  onSubmit: (data: PreChatData) => void;
}

function getInputType(field: PreChatFormField): string {
  switch (field.type) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    default:
      return 'text';
  }
}

export function PreChatForm({
  config,
  brandingColor,
  brandingName,
  brandingLogo,
  onSubmit,
}: PreChatFormProps) {
  const initial = Object.fromEntries(config.fields.map((f) => [f.name, '']));
  const [values, setValues] = useState<PreChatData>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const next = validateFields(config.fields, values);
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (validate()) {
      onSubmit(values);
    }
  }

  function handleChange(name: string, val: string) {
    setValues((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div
      class="flex-1 overflow-y-auto flex items-center justify-center p-5 transition-[background] duration-[350ms]"
      style={{
        backgroundColor: 'var(--w-bg)',
        backgroundImage: 'var(--w-bg-pattern)',
        backgroundSize: '20px 20px',
      }}
    >
      <div
        class={cn(
          'w-full max-w-[310px] rounded-[22px] p-6',
          'bg-w-bb-bg',
          'shadow-[0_10px_34px_rgba(0,0,0,0.12)]',
          'animate-pop-in',
          'flex flex-col items-center gap-4',
        )}
      >
        {/* Avatar */}
        {brandingLogo ? (
          <img
            src={brandingLogo}
            alt={brandingName || ''}
            class="w-[56px] h-[56px] rounded-full object-cover"
          />
        ) : (
          <div
            class="w-[56px] h-[56px] rounded-full flex items-center justify-center text-white text-[22px] font-bold"
            style={{ background: brandingColor }}
          >
            {(brandingName || '?').charAt(0).toUpperCase()}
          </div>
        )}

        {/* Name + subtitle */}
        {brandingName && (
          <div class="text-center">
            <p class="m-0 text-[15px] font-bold text-w-bb-fg">{brandingName}</p>
            <p class="m-0 text-[12.5px] text-w-secondary mt-0.5">
              {t('responds_instantly')} · 24/7
            </p>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          novalidate
          class="flex flex-col gap-3 w-full"
        >
          {config.fields.map((field) => (
            <div key={field.name} class="flex flex-col gap-1">
              <input
                id={`prechat-${field.name}`}
                name={field.name}
                type={getInputType(field)}
                value={values[field.name]}
                onInput={(e) =>
                  handleChange(field.name, (e.target as HTMLInputElement).value)
                }
                required={field.required}
                aria-required={field.required}
                aria-describedby={
                  errors[field.name] ? `prechat-${field.name}-err` : undefined
                }
                placeholder={`${field.label}${field.required ? ' *' : ` (${t('optional')})`}`}
                class={cn(
                  'rounded-[13px] px-3 py-2.5 text-[14px] text-w-bb-fg',
                  'border-[1.5px] outline-none transition-colors duration-150',
                  'bg-w-input placeholder:text-w-secondary/50',
                  errors[field.name]
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-w-border focus:border-[var(--brand)]',
                )}
              />
              {errors[field.name] && (
                <p
                  id={`prechat-${field.name}-err`}
                  class="m-0 text-xs text-red-500 px-1"
                  role="alert"
                >
                  {errors[field.name]}
                </p>
              )}
            </div>
          ))}

          {/* Validation hint */}
          {hasErrors && (
            <p class="m-0 text-[12.5px] text-red-500 text-center">
              {t('complete_required')}
            </p>
          )}

          <button
            type="submit"
            style={{ background: brandingColor }}
            class={cn(
              'mt-1 rounded-[14px] px-4 py-2.5 text-[15px] font-semibold text-white',
              'border-0 cursor-pointer w-full',
              'hover:opacity-90 active:scale-[0.98] transition-all duration-150',
              'focus-visible:ring-2 focus-visible:ring-offset-1',
            )}
          >
            {t('start_chat')}
          </button>
        </form>

        {/* Legal microcopy */}
        <p class="m-0 text-[10.5px] text-w-secondary/60 text-center leading-[1.4]">
          {t('prechat_legal', { name: brandingName || '' })}
        </p>
      </div>
    </div>
  );
}
